import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

interface CreateEditNodeModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: NodeFormData) => void;
  mode: 'create' | 'edit';
  saving?: boolean;
  saveError?: string | null;
}

export interface NodeFormData {
  id?: string;
  code: string;
  name: string;
  keywords: string[];
  semanticWeight: number;
  order: number;
}

const CreateEditNodeModal: React.FC<CreateEditNodeModalProps> = ({
  open,
  onClose,
  onSave,
  mode,
  saving = false,
  saveError = null,
}) => {
  const { selectedNode, loading } = useAppSelector((state) => state.nodes);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [keywords, setKeywords] = useState('');
  const [semanticWeight, setSemanticWeight] = useState<number>(0);
  const [order, setOrder] = useState<number>(0);

  useEffect(() => {
    if (mode === 'edit' && selectedNode) {
      setCode(selectedNode.code);
      setName(selectedNode.name);
      setKeywords(selectedNode.keywords?.join(', ') || '');
      setSemanticWeight(selectedNode.semanticWeight);
      setOrder(selectedNode.order);
    } else if (mode === 'create') {
      setCode('');
      setName('');
      setKeywords('');
      setSemanticWeight(0);
      setOrder(0);
    }
  }, [mode, selectedNode, open]);

  const handleSave = () => {
    const keywordsArray = keywords
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    const formData: NodeFormData = {
      code,
      name,
      keywords: keywordsArray,
      semanticWeight,
      order,
    };

    if (mode === 'edit' && selectedNode) {
      formData.id = selectedNode.id;
    }

    onSave(formData);
  };

  const isFormValid = code && name;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Crear Nuevo Nodo' : 'Editar Nodo'}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Código"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              margin="normal"
              required
              disabled={saving}
            />

            <TextField
              fullWidth
              label="Nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="normal"
              required
              disabled={saving}
            />

            <TextField
              fullWidth
              label="Palabras Clave (separadas por coma)"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              margin="normal"
              disabled={saving}
              helperText="Ingrese las palabras clave separadas por coma"
            />

            <TextField
              fullWidth
              label="Peso Semántico"
              type="number"
              value={semanticWeight}
              onChange={(e) => setSemanticWeight(Number(e.target.value))}
              margin="normal"
              disabled={saving}
            />

            <TextField
              fullWidth
              label="Orden"
              type="number"
              value={order}
              onChange={(e) => setOrder(Number(e.target.value))}
              margin="normal"
              disabled={saving}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving || !isFormValid}
        >
          {saving ? 'Guardando...' : mode === 'create' ? 'Crear' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditNodeModal;
